import { Icon } from "@circle-react-shared/Icon";
import { Button as CompassButton } from "@circleco/compass/components/Button";
import { useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { invalidateAllMemberQueries } from "@/react/components/ProfileModalV3/Body/ConnectBanner/util";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { useConnectModal } from "@/react/components/shared/ConnectModal/ConnectModal";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useCreateConnection } from "@/react/hooks/connections/useCreateConnection";
import { useCircleV4 } from "@/react/hooks/useCircleV4";
import { useThemeContext } from "@/react/providers/Theme";

import type { ConnectButtonProps } from "./types";

const sizeClasses = {
  sm: "!h-7 !px-3 gap-1.5",
  md: "!h-8 !px-4 gap-2",
  lg: "!h-10 !px-5 gap-2.5",
  full: "w-full",
};

const iconSizes = {
  sm: 14,
  md: 16,
  lg: 18,
  full: 16,
};

export const ConnectButton = ({
  recipient,
  recommendationId,
  size = "md",
  className,
  disabled,
  fullWidth = false,
  buttonVariant = "secondary",
  onClick,
}: ConnectButtonProps) => {
  const { isCircleV4 } = useCircleV4();
  const toast = useToast();
  const queryClient = useQueryClient();
  const connectModal = useConnectModal();
  const { isDarkAppearance } = useThemeContext();

  const getTextColor = () => {
    if (buttonVariant === "circle") {
      return isDarkAppearance ? "text-badge" : "text-white";
    }
    return undefined;
  };

  const { mutate: createConnection, isPending } = useCreateConnection({
    onSuccess: async () => {
      await invalidateAllMemberQueries(queryClient, recipient.public_uid ?? "");
      toast.success(
        t("profile_drawer.connect_banner.messages.connection_created"),
      );
    },
    onError: error => {
      if (error && typeof error === "object" && "message" in error) {
        toast.error(String(error.message));
      } else {
        toast.error(
          t("profile_drawer.connect_banner.messages.connection_creation_error"),
        );
      }
    },
  });

  const handleConnect = (note?: string) => {
    createConnection({ memberId: recipient.id, note, recommendationId });
  };

  const openConnectModal = () => {
    void connectModal.show({
      recipient,
      onConnect: handleConnect,
    });
  };

  const handleButtonClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onClick?.();
    openConnectModal();
  };

  if (isCircleV4) {
    const compassVariant = buttonVariant === "circle" ? "primary" : "outline";
    return (
      <CompassButton
        variant={compassVariant}
        startIcon="people-add"
        loading={isPending}
        disabled={disabled}
        onClick={handleButtonClick}
        aria-label={t("connect")}
        data-testid="connect-button"
        fullWidth={fullWidth}
        className={className}
      >
        {t("connect")}
      </CompassButton>
    );
  }

  const iconSize = iconSizes[size];

  return (
    <Button
      variant={buttonVariant}
      onClick={handleButtonClick}
      aria-label={t("connect")}
      data-testid="connect-button"
      large={size === "lg"}
      className={classNames(
        "!border-primary !font-medium flex justify-center items-center !gap-1 min-w-30",
        sizeClasses[size],
        className,
      )}
      disabled={disabled || isPending}
    >
      {isPending ? (
        <Icon type="loader" />
      ) : (
        <>
          <Icon type="16-plus-v2" size={iconSize} useWithFillCurrentColor />
          <Typography.LabelSm weight="font-medium" color={getTextColor()}>
            {t("connect")}
          </Typography.LabelSm>
        </>
      )}
    </Button>
  );
};
